import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import FilePreview, { formatReaderBytes } from "../components/FilePreview";
import { LANGUAGE_LABEL, detectLanguage } from "../components/fileLanguage";
import { resolveRelativeAssetPath } from "../../../utils/markdownRenderer";

import { useRemoteFileMetadata } from "../../../hooks/useRemoteFileMetadata";
import { useRemoteFileContent } from "../../../hooks/useRemoteFileContent";
import {
  supabaseAuthenticatedApi,
  workspaceAgentUrl,
} from "../../../components/SupabaseClient";

export default function StorageFileReader({ filePath, source = "home" }) {
  const [forceOpen, setForceOpen] = useState(false);
  const {
    metadata,
    loading: metaLoading,
    error: metaError,
  } = useRemoteFileMetadata(source, filePath);
  const {
    contentData,
    error: contentError,
    status: contentStatus,
    warning,
    canForceOpen,
  } = useRemoteFileContent(source, filePath, metadata, { forceOpen });

  useEffect(() => {
    setForceOpen(false);
  }, [filePath, source]);

  const fileName =
    metadata?.fileName || (filePath ? filePath.split("/").pop() : "");
  const language = useMemo(
    () => detectLanguage(fileName || filePath || ""),
    [fileName, filePath],
  );
  const content = contentData?.content || "";
  const lineCount = useMemo(() => {
    if (typeof contentData?.lineCount === "number")
      return contentData.lineCount;
    return content ? content.replace(/\r/g, "").split("\n").length : 0;
  }, [content, contentData?.lineCount]);

  const baseDir =
    filePath && filePath.includes("/")
      ? filePath.slice(0, filePath.lastIndexOf("/"))
      : "";

  const resolveAssetUrl = useCallback(
    async (relativeSrc) => {
      const resolvedPath = resolveRelativeAssetPath(baseDir, relativeSrc);
      const response = await supabaseAuthenticatedApi.post(
        "/filesystem/read-raw-content",
        { scope: source, path: resolvedPath },
        workspaceAgentUrl,
        15000,
      );
      if (!response) return null;
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    },
    [source, baseDir],
  );

  if (!filePath) return null;

  return (
    <FilePreview
      fileName={fileName}
      filePath={metadata?.filePath || filePath}
      language={language}
      languageLabel={LANGUAGE_LABEL[language] || "Text"}
      content={content}
      sizeText={metadata ? formatReaderBytes(metadata.size) : ""}
      lineCount={lineCount}
      loading={metaLoading || contentStatus === "loading"}
      error={metaError || contentError}
      previewBlocked={contentStatus === "blocked"}
      warning={warning}
      canForceOpen={canForceOpen}
      onForceOpen={() => setForceOpen(true)}
      resolveAssetUrl={resolveAssetUrl}
    />
  );
}

StorageFileReader.propTypes = {
  filePath: PropTypes.string,
  source: PropTypes.oneOf(["home", "datasets"]),
};
